import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo, { TicketTagsBar } from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import TicketMediaGallery from "../TicketMediaGallery";
import AiPendingActionCard from "../AiPendingActionCard";
import { Button } from "../ui/button";
import { Files } from "lucide-react";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";
import useTecladoVirtual from "../../hooks/useTecladoVirtual";

const Ticket = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [galeriaAberta, setGaleriaAberta] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, navigate]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      // Mesma armadilha do MessagesList: o evento "ticket" também vai para as
      // salas de status e de notificação, e a conexão do Socket.IO é uma só
      // para o app inteiro. Sem conferir o id, qualquer mexida em OUTRO
      // atendimento (alguém aceitar, transferir, encerrar) trocava o
      // cabeçalho e os botões desta tela pelos do ticket alheio — e o
      // "delete", que o UpdateTicketService dispara a cada mudança de status,
      // expulsava quem estava lendo a conversa com um "Ticket deleted".
      if (data.action === "update") {
        if (Number(data.ticket?.id) !== Number(ticketId)) return;
        setTicket(data.ticket);
      }

      // `motivo: "status"` é o ticket saindo de uma lista (foi aceito,
      // transferido, encerrado) — continua existindo, e quem está lendo não
      // pode ser expulso da conversa por isso.
      if (data.action === "delete" && data.motivo !== "status") {
        if (Number(data.ticketId) !== Number(ticketId)) return;
        toast.success("Ticket deleted sucessfully.");
        navigate("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, navigate]);

  // Ao digitar no celular o teclado cobre a parte de baixo da tela, mas o
  // `h-dvh` do layout continua contando a tela inteira: a caixa de digitar
  // ficava atrás do teclado e o cabeçalho saía por cima. Amarrar a altura
  // desta tela à faixa visível devolve o comportamento de aplicativo de
  // mensagem — cabeçalho colado no topo, caixa de texto colada no teclado e
  // só a conversa rolando no meio.
  const { alturaVisivel, deslocamento, tecladoAberto } = useTecladoVirtual();

  const estiloTeclado = tecladoAberto
    ? {
        height: `${alturaVisivel}px`,
        // O navegador empurra a página para cima para revelar o campo focado;
        // sem devolver esse deslocamento o cabeçalho fica fora da tela.
        transform: `translateY(${deslocamento}px)`,
      }
    : undefined;

  // Encolher a área visível não move o rolamento da conversa: a última
  // mensagem, que estava no fim, some atrás da caixa de texto. Recolar no fim
  // quando o teclado abre (e a cada mudança de altura) mantém à vista o que a
  // pessoa estava lendo.
  useEffect(() => {
    if (!tecladoAberto) return;
    const lista = document.getElementById("messagesList");
    if (lista) lista.scrollTop = lista.scrollHeight;
  }, [tecladoAberto, alturaVisivel]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div
      id="drawer-container"
      className="relative flex h-full w-full overflow-hidden"
      style={estiloTeclado}
    >
      <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-background">
        <TicketHeader loading={loading}>
          <div className="relative flex min-w-0 flex-1 items-center">
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          {/* Etiquetas ficam na fila de ações, e não mais penduradas sobre a
              foto do contato: ali eram 28px sem rótulo por cima do avatar, e
              quem procurava como etiquetar um atendimento concluía que o
              sistema não etiquetava. Fora do TicketInfo porque ele é um
              <button> — botão dentro de botão é HTML inválido e o clique de
              um engole o do outro. */}
          {!loading && ticket.id && <TicketTagsBar ticket={ticket} />}
          {/* Atalho para as imagens da conversa. Fica antes das ações porque
              é leitura — não muda o estado do atendimento. */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setGaleriaAberta(true)}
            title="Arquivos do atendimento"
            aria-label="Arquivos do atendimento"
            className="shrink-0"
          >
            <Files className="h-5 w-5" />
          </Button>
          <TicketActionButtons ticket={ticket} />
        </TicketHeader>

        <ReplyMessageProvider>
          <MessagesList
            ticketId={ticketId}
            isGroup={ticket.isGroup}
          ></MessagesList>
          {/* A IA pode ter pedido uma ação que depende do atendente. O cartão
              fica acima do campo de mensagem: é ali que quem está lendo a
              conversa decide. */}
          <AiPendingActionCard ticketId={ticketId} />
          <MessageInput ticketStatus={ticket.status} isGroup={ticket.isGroup} />
        </ReplyMessageProvider>
      </div>
      <TicketMediaGallery
        open={galeriaAberta}
        onClose={() => setGaleriaAberta(false)}
        ticketId={ticketId}
      />
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticket={ticket}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
